// @vendors
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { i18n } from 'core/i18n'
import classNames from 'classnames';
import './Step1ContractSavings.scss'

// @components
import RiskProduct from 'components/riskProduct/riskProduct'
import { offersAccountsAdapter, getNamedVariables } from 'components/offers/adapters/singleAccountAdapter';
import { OKButton } from 'components/commons/OKButton/OKButton';
import { OKLink } from 'commonsComponents/OKLink';

// @helpers
import { getIsFullProfitability } from 'utilities/depositHelper'

// @constants
import {
    SAVINGS_WITHOUT_CONDITION_WITHOUT_TERM,
    SAVINGS_WITHOUT_CONDITION_WITH_TERM,
    SAVINGS_WITH_CONDITION_WITH_TERM,
    SAVINGS_WITH_CONDITION_WITHOUT_TERM
} from 'constants/eAccountLoan'
import { E_ACCOUNT_LOAN_PRODUCT_ALIAS } from 'constants/hiringProductInformation'
import { POE_KEYS } from './constants/poeKeys'

const { formatText, FormattedText, FormattedTextWithNamedVariables } = i18n;

const Step1ContractSavings = ({ immProfileFields, immFlagBasedFeatures, mainAccount, immContract, ...props }) => {
    const originCountry = immProfileFields.get('originCountry');
    const showFullProfitability = !getIsFullProfitability(originCountry)

    const parsedMainAccount = mainAccount.toJS()

    const { data: { accountDefinition = {}, accountConfig = {} } = {} } = parsedMainAccount

    useEffect(() => {
        const { localState, updateConfirmationCheckboxes } = props;
        const { confirmationCheckboxes } = localState;
        const dicLinkUrl = accountConfig.precontractualDicUrl;
        if (dicLinkUrl) {
            confirmationCheckboxes.list[0].labels[3].href = dicLinkUrl;
            updateConfirmationCheckboxes(confirmationCheckboxes);
        }
    }, [accountConfig.precontractualDicUrl])

    useEffect(()=> {
        const { localState, updateCardDetails, getPrecontractualDocumentURLOrID, productId } = props;
        const { card_details } = localState;

        const precontractualDocumentId = immContract.get('precontractualDocumentId')
        const precontractualDocumentUrl = immContract.get('precontractualDocumentUrl')

        if (productId && (!precontractualDocumentId && !precontractualDocumentUrl)) {
            getPrecontractualDocumentURLOrID({
                payload: {
                    "metadata": {
                        "productId": productId,
                        "language": originCountry
                    },
                    "precontractualType": E_ACCOUNT_LOAN_PRODUCT_ALIAS
                }
            })
        }

        if (accountDefinition && accountDefinition.accountType) {
            const accountType = accountDefinition.accountType 

            card_details.find(x => x.id === 'tin').value = accountConfig.tin
            card_details.find(x => x.id === 'tinConfirmationStep').value = accountConfig.tin

            if (!showFullProfitability) {
                card_details.find(x => x.id === 'tae').value = accountConfig.tae
            }

            if ([SAVINGS_WITH_CONDITION_WITH_TERM, SAVINGS_WITHOUT_CONDITION_WITH_TERM].includes(accountType)) {
                card_details.find(x => x.id === 'account_term').value = 'contractsView-installmentValueKey';
                card_details.find(x => x.id === 'account_term').injectedStringValues = [accountConfig.bonificationTerm];
            } else {
                card_details.find(x => x.id === 'account_term').visible = []
                card_details.find(x => x.id === 'account_term').confirmation_step = null
            }

            if ([SAVINGS_WITHOUT_CONDITION_WITHOUT_TERM, SAVINGS_WITH_CONDITION_WITHOUT_TERM].includes(accountType) || showFullProfitability || __CONTACT_CENTER__) {
                card_details.find(x => x.id === 'cta_seleccionada').confirmation_step = {
                    ...card_details.find(x => x.id === 'cta_seleccionada').confirmation_step,
                    row: 1
                }
            }
            updateCardDetails(card_details)
        }
    }, [mainAccount]);

    const handleLinkClick = () => {
        const {
            getDocumentsOkApiById,
            getPrecontractualDocumentURLOrID,
            productId
        } = props;

        getPrecontractualDocumentURLOrID({
            payload: {
                "metadata": {
                    "productId": productId,
                    "language": originCountry
                },
                "precontractualType": E_ACCOUNT_LOAN_PRODUCT_ALIAS
            },
            onDocumentURLCallback: (url)=>{
                window.open(url, '_blank')
            },
            onDocumentIDCallback: (id)=>{
                getDocumentsOkApiById(id, undefined, true)
            }
        })
    }

    const tin = accountConfig.tin;
    const tae = accountConfig.tae

    const dicLinkUrl = accountConfig.precontractualDicUrl;

    const accountData = offersAccountsAdapter(parsedMainAccount.data);

    const getProfitabilityKeys = React.useCallback(() => {
        try {
            const poeKeys = POE_KEYS[accountDefinition.accountType]
            return poeKeys.profitabilities.map((profitability, index) => (
                <React.Fragment key={profitability.rate}>
                    {
                        profitability.rate && (
                            <li>
                                <FormattedTextWithNamedVariables value={profitability.rate} namedVariables={getNamedVariables(profitability.rate, { ...accountConfig, superIndex: index + 1 })} />
                            </li>
                        )
                    }
                    {
                        profitability.condition && (
                            <li>
                                <FormattedTextWithNamedVariables value={profitability.condition} namedVariables={getNamedVariables(profitability.condition, { ...accountConfig })} />
                            </li>
                        )
                    }
                </React.Fragment>
            ))
        } catch {
            return []
        }
    }, [mainAccount])

    const profitabilities = getProfitabilityKeys();

    const tinText = !showFullProfitability ? 
    "contractsView-contractsView_accountSavingStep1Tin" : 
    "contractsView-contractsView_accountSavingStep1OnlyTin";
    
    const tinClassName = classNames('step1ContractSavings__product__description__tin', {
        'step1ContractSavings__product__description__tin--only-tin': showFullProfitability
    });

    return (
        <>
            <RiskProduct
                immFlagBasedFeatures={immFlagBasedFeatures}
                isAlertLiquidity={false}
            />
            <h1 className="step1ContractSavings__title">
                <FormattedText value={`${accountDefinition.context}-${accountConfig.name}`} />
            </h1>
            <p className="step1ContractSavings__subtitle">
                <FormattedText value={`${accountDefinition.context}-${accountConfig.subtitle}`} />
            </p>
            <div className="step1ContractSavings__product">
                <div className="step1ContractSavings__product__description">                    
                    {(!showFullProfitability) &&
                        <p className="step1ContractSavings__product__description__tae">
                            <FormattedText value="contractsView-contractsView_accountSavingStep1Tae" injectedStrings={[tae]} />
                        </p>
                    }
                    <p className={tinClassName}>
                        <FormattedText value={tinText} injectedStrings={[tin]} />
                    </p>
                </div>
                <div className="step1ContractSavings__product__advantages">
                    <h5>
                        <FormattedText value="contractsView-contractsView_accountSavingProfitability" />
                    </h5>
                    <ul>
                        {profitabilities}
                    </ul>
                    <h5>
                        <FormattedText value="contractsView-contractsView_accountSavingAdvantages" />
                    </h5>
                    <ul>
                        {accountData.ADVANTAGE.map((advantage, index) => <li key={index}>{advantage}</li>)}
                    </ul>
                    {
                        accountData.CONDITION.length > 0 && (
                            <>
                                <h5>
                                    <FormattedText value="contractsView-contractsView_accountSavingConditions" />
                                </h5>
                                <ul>
                                    {accountData.CONDITION.map((condition, index) => <li key={index}>{condition}</li>)}
                                </ul>
                            </>
                        )
                    }
                </div>
            </div>
            <div className="step1ContractSavings__documents-link">
                <OKButton  modifier="link-complementary" onClick={handleLinkClick}>
                    {formatText('contractsView-contractsView_accountSavingStep1AdvantagesLinkPreContractual')}
                </OKButton>
                <div>
                    <OKLink className="step1ContractSavings__document-link" modifier="complementary" to={dicLinkUrl} target="_blank">
                        {formatText('contractsView-contractsView_accountSavingStep1AdvantagesLinkDIC')}
                    </OKLink>
                </div>
            </div>
        </>
    );
}

Step1ContractSavings.defaultProps = {
    immAccounts: null,
    showBlockedAccountMessage: false,
    getDocumentsOkApiById: () => {},
    mainAccount: null
};

Step1ContractSavings.propTypes = {
    getDocumentsOkApiById: PropTypes.func.isRequired,
    immFlagBasedFeatures: PropTypes.object.isRequired,
    immContract: PropTypes.object.isRequired,
    immAccounts: PropTypes.object,
    immDeposits: PropTypes.object,
    immProfileFields: PropTypes.object,
    showBlockedAccountMessage: PropTypes.bool,
    mainAccount: PropTypes.object,
    updateConfirmationCheckboxes: PropTypes.func.isRequired
}

module.exports = Step1ContractSavings
